<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  // SZ: contador (Figma) — exibe pill cinza ao lado direito quando > 0.
  count: { type: Number, default: 0 },
  // SZ: marca item como "canal desconectado" (Figma) — bg/texto vermelho-suave
  disconnected: { type: Boolean, default: false },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ltr:pl-3 rtl:pr-3 rtl:mr-3 ltr:ml-3 relative text-n-slate-11 child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="flex h-8 items-center gap-2 px-2 py-1 rounded-lg max-w-[9.438rem] hover:bg-gradient-to-r from-transparent via-n-slate-3/70 to-n-slate-3/70 group"
      :class="{
        'text-n-blue-text bg-n-alpha-2 active': active,
        'sz-leaf-disconnected': disconnected && !active,
      }"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        :label
        :icon
        :active
        :count
        :disconnected
      />
      <template v-else>
        <Icon v-if="icon" :icon="icon" class="size-4 inline-block" />
        <div class="flex-1 truncate min-w-0">{{ label }}</div>
        <span
          v-if="count > 0"
          class="sz-side-count"
          :class="{ 'sz-side-count--active': active }"
        >{{ count }}</span>
      </template>
    </component>
  </Policy>
</template>

<style scoped>
/* SZ: badge contador (Figma) — pill cinza ao lado direito do item da sidebar */
.sz-side-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 20px;
  padding: 0 4px;
  background: #ECECEC;
  border-radius: 4px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  color: #525252;
  flex-shrink: 0;
}
.sz-side-count--active {
  background: #C0DDC7;
  color: #236D23;
}
/* Dark mode */
:global(.dark) .sz-side-count {
  background: #2A2D31;
  color: #B8BBC1;
}
:global(.dark) .sz-side-count--active {
  background: #1F3A24;
  color: #7DBA80;
}
/* SZ: canal desconectado (Figma) — light */
.sz-leaf-disconnected {
  background: #FBEFEF !important;
  color: #9F0712 !important;
}
.sz-leaf-disconnected :deep(svg) {
  color: #9F0712;
}
</style>

<!-- Bloco não-scoped: o Vue scoped CSS compiler estraga regras
     que combinam :global() + :deep() na mesma cascata. Aqui ficam
     as regras dark do estado desconectado, sem scoping. -->
<style>
.dark .sz-leaf-disconnected {
  background: #442828 !important;
  color: #D99090 !important;
}
.dark .sz-leaf-disconnected svg {
  color: #D99090 !important;
}
</style>
